// Hotellkapasitet (/hotellkapasitet, Verktøy i tannhjulmenyen): plassene i sykkelhotellet og
// dekkhotellet, som varierer med sesong og lager (Anders 30.10). Samme oppsett som «Kapasitet»
// under Tjenester og priser. Alle ansatte kan se; bare leder og mekaniker kan endre (backenden
// sjekker det samme, src/middleware/lederEllerMekaniker.js).
import React, { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { Tilbake, Sidetopp, knappPrimar } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import { useKanEndrePriser } from '../employeeAccess';
import { sistEndret } from './tjenester/katalogFelles';
import { Tallfelt, Melding, Endringslogg } from './tjenester/Velgere';
import { LAGERSTED_LABEL } from '../constants/dekkhotell';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

async function kall(sti, metode = 'GET', data) {
  const res = await fetch(`${API_BASE_URL}/${sti}`, {
    method: metode,
    headers: data
      ? { 'Content-Type': 'application/json', 'X-Ansatt': encodeURIComponent(Cookies.get('selectedEmployee') || '') }
      : {},
    body: data ? JSON.stringify(data) : undefined,
  });
  const svar = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(svar.message || (metode === 'GET' ? 'Klarte ikke å hente plassene.' : 'Klarte ikke å lagre. Prøv igjen.'));
  return svar;
}

function Status({ status }) {
  if (status.type === 'feil') return <Melding type="feil">{status.tekst}</Melding>;
  if (status.type === 'ok') return <Melding type="ok">{status.tekst}</Melding>;
  return null;
}

function Lagreknapp({ lagrer }) {
  return (
    <button type="submit" disabled={lagrer} className={knappPrimar}>
      <Ikon navn="hake" className="w-6 h-6" /> {lagrer ? 'Lagrer …' : 'Lagre'}
    </button>
  );
}

const kort = 'space-y-4 rounded-2xl border-2 border-slate-200 bg-white p-3';

function Sykkelhotell({ kap, kanEndre, onLagret }) {
  const [plasser, setPlasser] = useState(kap.plasser);
  const [status, setStatus] = useState({ type: '', tekst: '' });
  const tatt = kap.opptatt + kap.reservert;

  async function lagre(e) {
    e.preventDefault();
    setStatus({ type: 'lagrer', tekst: '' });
    try {
      onLagret(await kall('sykkelhotell/kapasitet', 'PATCH', { plasser }));
      setStatus({ type: 'ok', tekst: 'Lagret' });
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  return (
    <form onSubmit={lagre} aria-labelledby="hk-sykkel-tittel" className={kort}>
      <h2 id="hk-sykkel-tittel" className="text-lg font-bold text-slate-900">Sykkelhotell</h2>
      <p className="text-slate-700">
        Nå: {kap.opptatt} på lageret eller på vei dit · {kap.reservert} reservert fra nettbookinger ·{' '}
        <strong>{kap.ledige} ledige</strong>
      </p>
      <fieldset disabled={!kanEndre} className="min-w-0 space-y-3">
        <legend className="sr-only">Plasser i sykkelhotellet</legend>
        <Tallfelt
          id="hk-sykkel-plasser"
          etikett="Plasser på lageret"
          hjelp="Når alle plassene er tatt eller reservert, viser nettsiden «Fullt nå», og nye sykler kan ikke legges inn."
          verdi={plasser}
          onEndre={setPlasser}
          enhet="sykler"
        />
        {typeof plasser === 'number' && plasser < tatt && (
          <Melding>
            {tatt} plasser er tatt eller reservert. Med {plasser} plasser er sykkelhotellet fullt til nok sykler er hentet.
          </Melding>
        )}
      </fieldset>
      <Status status={status} />
      {kanEndre && <Lagreknapp lagrer={status.type === 'lagrer'} />}
      <p className="text-sm text-slate-600">{sistEndret(kap) || 'Ikke endret her ennå (15 plasser fra start).'}</p>
      <Endringslogg endringer={kap.endringer} />
    </form>
  );
}

function Dekkhotell({ kap, kanEndre, onLagret }) {
  const [perSted, setPerSted] = useState(kap.perSted);
  const [status, setStatus] = useState({ type: '', tekst: '' });

  async function lagre(e) {
    e.preventDefault();
    setStatus({ type: 'lagrer', tekst: '' });
    // Bare stedene som er endret, så loggen ikke får rader for felt ingen har rørt.
    const endret = Object.fromEntries(Object.entries(perSted).filter(([s, v]) => v !== kap.perSted[s]));
    try {
      onLagret(await kall('dekkhotell/kapasitet', 'PATCH', { perSted: endret }));
      setStatus({ type: 'ok', tekst: 'Lagret' });
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  return (
    <form onSubmit={lagre} aria-labelledby="hk-dekk-tittel" className={kort}>
      <h2 id="hk-dekk-tittel" className="text-lg font-bold text-slate-900">Dekkhotell</h2>
      <p className="text-slate-700">
        Antall dekk- eller hjulsett hvert sted har plass til. Tomt felt: ingen grense. Innlagring stoppes ikke når et sted
        er fullt, men den som legger inn får beskjed om å velge et annet sted.
      </p>
      <fieldset disabled={!kanEndre} className="min-w-0">
        <legend className="sr-only">Plasser i dekkhotellet per sted</legend>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(LAGERSTED_LABEL).map(([sted, navn]) => {
            const iBruk = kap.iBruk?.[sted] || 0;
            const grense = perSted[sted];
            return (
              <Tallfelt
                key={sted}
                id={`hk-dekk-${sted}`}
                etikett={navn}
                hjelp={`${iBruk} sett der nå${typeof grense === 'number' && iBruk >= grense ? ' · fullt' : ''}`}
                verdi={grense}
                onEndre={(v) => setPerSted((p) => ({ ...p, [sted]: v }))}
                enhet="sett"
              />
            );
          })}
        </div>
      </fieldset>
      <Status status={status} />
      {kanEndre && <Lagreknapp lagrer={status.type === 'lagrer'} />}
      <p className="text-sm text-slate-600">{sistEndret(kap) || 'Ikke satt ennå.'}</p>
      <Endringslogg endringer={kap.endringer} />
    </form>
  );
}

export default function Hotellkapasitet() {
  const kanEndre = useKanEndrePriser();
  const [sykkel, setSykkel] = useState(null);
  const [dekk, setDekk] = useState(null);
  const [feil, setFeil] = useState('');

  const hent = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([kall('sykkelhotell/kapasitet'), kall('dekkhotell/kapasitet')]);
      setSykkel(s);
      setDekk(d);
      setFeil('');
    } catch (err) {
      setFeil(err.message);
    }
  }, []);
  useEffect(() => { hent(); }, [hent]);

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />
      <Sidetopp ikon="hotell" farge="lager" tittel="Hotellkapasitet"
        undertittel="Plassene i sykkelhotellet og dekkhotellet. Endres når sesongen eller lageret endrer seg." />

      {!kanEndre && (
        <Melding>Du kan se plassene. Bare leder og mekaniker kan endre dem; velg deg selv øverst i menyen.</Melding>
      )}
      {feil && <Melding type="feil">{feil}</Melding>}
      {!feil && (!sykkel || !dekk) && (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter plassene …</p>
      )}

      {sykkel && <Sykkelhotell kap={sykkel} kanEndre={kanEndre} onLagret={setSykkel} />}
      {dekk && <Dekkhotell kap={dekk} kanEndre={kanEndre} onLagret={setDekk} />}
    </div>
  );
}
